/**
 * Per-table feed watchdog: notices when ONE table's update stream has died while
 * the connection carrying it is still healthy, and asks for exactly one repair.
 *
 * Why a silence rule is sound here (it was not always). Every table frame the
 * React client renders comes from the cash-game-broadcast Lambda over the update
 * socket, and on that path the engine enqueues every open table every tick, a
 * publisher-side lock collapses that to at most one message per table per ~3s,
 * and neither the processor nor the broadcaster change-gates. So a live table
 * emits a frame at least every few seconds even when nothing is happening on it,
 * and even when the engine has it stuck. (The engine's own change-gated
 * `processTexasTableUpdate` path is dead code: nothing publishes from it.) A
 * table that has gone quiet for several multiples of that cadence has lost its
 * feed. The feed is a DynamoDB row keyed by (table, connection id); the ways a
 * single row goes missing with the connection alive are documented in the RCA.
 *
 * Why it must be bounded the way it is. A stalled table would once have looked
 * like a dead feed to a naive silence check, and a fleet of clients all
 * "repairing" a stall at once is the reconnect storm this platform has already
 * lived through. Here a stalled table still emits frames (with a frozen state),
 * so it never trips the rule at all - and even if some table did, the rules
 * below bound the worst case to a few requests per table per silence episode,
 * spread out, and to a fixed ceiling per client per rolling window:
 *
 *   1. The only action is a targeted re-subscribe of that one table on the
 *      update socket. There is NO reconnect path in here: connection death is
 *      the keepalive's job (missed pongs force a reconnect), and reconnecting
 *      would blank every table to fix one.
 *   2. A small number of repairs per silence episode (3), then nothing until a
 *      frame arrives - which is also the only proof a repair worked. It is not
 *      one, because a repair is only a REQUEST: the subscribe Lambda can reject
 *      it and reports nothing, so spending the episode on the send left a
 *      rejected repair's table dead for the life of the connection.
 *   3. Random jitter before firing, so clients that hit the threshold together
 *      spread their requests out. The width matters: it sets the PEAK rate of
 *      a fleet-wide burst, and a client whose feed recovers inside its wait
 *      never fires at all.
 *   4. A per-table floor between repairs, and a client-wide breaker on the total
 *      number of repairs in a rolling window. Both DEFER rather than cancel: a
 *      blocked table stays pending and fires once the floor or the window
 *      allows, so every dead table is eventually repaired.
 *
 *      tick() offers tables awaiting their FIRST repair ahead of tables asking
 *      for a repeat. Be precise about what that buys, because an earlier version
 *      of this comment overclaimed it: it resolves contention WITHIN a tick, not
 *      across time. It cannot help when a repeat has already banked a slot
 *      before the other table went silent at all - one table dead from the start
 *      spends three slots at 20s/80s/140s, and a table that dies at 200s then
 *      waits for the oldest of those to age out of the window. Measured, with
 *      four tables and one dead from the start: first repairs at 20s, 220s,
 *      320s, 380s, against 220s for all three late tables when the episode
 *      allowance was 1. So allowing repeats costs a late table 100-160s on its
 *      FIRST repair. The total request count is unchanged either way
 *      (episode allowance x tables), and so is the peak rate, which the 60s
 *      per-table floor sets rather than the breaker - the breaker only decides
 *      whether a repair happens now or a couple of minutes later.
 *
 * Pure + framework-free. The owner (ConnectionManager) feeds it frames and
 * subscriptions, ticks it on a timer, and performs the repairs it names.
 */

/**
 * All timestamps handed to this module are MONOTONIC milliseconds
 * (`performance.now()`), never wall clock.
 *
 * Wall clock can jump backwards (NTP correction) or forwards (device sleep then
 * wake, which this app does on every Capacitor foreground). A backwards jump
 * would push a pending `dueAt` into the far future and silently disable the
 * repair; a forwards jump would fire every pending table on one tick. This
 * codebase has already been bitten by that class of Capacitor edge case, so the
 * accounting uses a clock that only moves forward. Wall clock is still fine for
 * log lines - it just never decides anything here.
 */
export type TableFeedWatchdogOptions = {
  /** How long a tracked table may be silent before it is considered dead. */
  silenceMs?: number;
  /** Upper bound on the random delay added before a repair fires. */
  jitterMs?: number;
  /** Minimum gap between two repairs of the same table. */
  minRepairGapMs?: number;
  /** Breaker: at most this many repairs, across all tables, per `windowMs`. */
  maxRepairsPerWindow?: number;
  /** At most this many repairs for one table per silence episode. */
  maxRepairsPerEpisode?: number;
  /**
   * Silence threshold for the PRIORITY table - the one the player is acting on.
   * Much tighter than `silenceMs`; see setPriority().
   */
  prioritySilenceMs?: number;
  /** Jitter for the priority table's repair. Narrower than `jitterMs`. */
  priorityJitterMs?: number;
  /**
   * How long a table that had ALREADY proven a feed may stay frameless after a
   * reconnect before it is judged dead. Shorter than `silenceMs` on purpose -
   * see resetClock().
   */
  postReconnectGraceMs?: number;
  /**
   * How long after a repair a still-frameless table waits before it is reported
   * by `exhaustedTables()` as beyond this watchdog's remedy. See that method.
   */
  escalateAfterRepairMs?: number;
  windowMs?: number;
  /** Injectable for tests. Must return [0, 1). */
  random?: () => number;
};

type TableFeedState = {
  /**
   * Monotonic time of the last frame, or null while the table has no live feed
   * to measure - either it has NEVER produced one, or a reconnect cleared it.
   * Which of those it is, is `everHadFrame`.
   */
  lastFrameAt: number | null;
  /**
   * This table has produced at least one frame at some point in this watchdog's
   * life, on this or an earlier connection.
   *
   * The distinction this draws is the one EN-10628's QA pass fell through. A
   * table that has NEVER hydrated is not this watchdog's problem (a targeted
   * re-subscribe cannot deliver a first frame - see track()). A table that HAD a
   * feed and lost it across a reconnect very much is: its subscription is
   * genuinely new on the fresh connectionId, so a re-subscribe DOES make the
   * Lambda write the row and push a snapshot. Before this flag existed,
   * resetClock() nulled `lastFrameAt` and both cases looked identical, so a
   * reconnect whose re-subscribes were rejected left every table permanently
   * invisible here - measured in develop as 71s of a completely frameless client
   * with the watchdog never firing once.
   */
  everHadFrame: boolean;
  /**
   * While set, the table is inside its post-reconnect grace window and is judged
   * against this instead of `silenceMs`. Null outside one.
   */
  graceUntil: number | null;
  /** Repairs already sent during the current silence. Cleared by a frame. */
  episodeRepairs: number;
  /** When the pending repair may fire (threshold + jitter), or null. */
  dueAt: number | null;
  lastRepairAt: number;
  /** Reported by exhaustedTables() already, so it is reported once per episode. */
  escalated: boolean;
};

export type TableFeedRepair = {
  gameID: string;
  /** How long the table had been silent when the repair fired. */
  silentForMs: number;
};

// Roughly seven times the live cadence. The watchdog protects the tables the
// player is NOT acting on (the acting turn is covered by PokerActions' own
// re-assert at ~3s), so the threshold trades detection latency on a genuinely
// dead background feed against false positives during a platform hiccup: a
// hiccup shorter than this never triggers anything at all.
export const DEFAULT_FEED_SILENCE_MS = 20_000;
// Sized against production: at the observed peak of ~1,235 connections a
// fleet-wide burst is ~1,850 subscribe calls; over 10s that is ~185/s, about 28
// concurrent at the Lambda's 151ms p95, against an observed ceiling of 70 with
// zero throttles (2026-09-02, 24h). Wider jitter buys little; narrower eats
// the margin. Worst-case detection for a dead background table is 20s + 10s.
export const DEFAULT_FEED_JITTER_MS = 10_000;
export const DEFAULT_MIN_REPAIR_GAP_MS = 60_000;
export const DEFAULT_MAX_REPAIRS_PER_WINDOW = 4;
// More than one repair per silence episode, because a repair is only a REQUEST:
// the subscribe Lambda can reject it (409 at the four-table cap, 404 on a
// missing table or player) and it tells the client nothing either way. Spending
// the episode on the send meant a rejected repair left that table dead for the
// life of the connection, with the watchdog believing it had done the work (PR
// review finding 7). Combined with the per-table floor this is at most one
// request per minute per dead table, and the breaker still caps the total.
export const DEFAULT_MAX_REPAIRS_PER_EPISODE = 3;
// The table the player is ACTING on is judged on a far tighter clock than the
// background tables, because the cost of being slow there is a missed turn.
//
// 20s was chosen for background tables on the reasoning that "the acting turn is
// covered by PokerActions' own re-assert at ~3s". That is only true AFTER the
// player presses something - a feed that dies on the table they are about to act
// on has nothing watching it, so detection took silenceMs + jitter + a tick:
// 20-35s, which at the 17s production clock is two or more missed turns. That is
// the gap this closes.
//
// 6s is ~1.7x the worst healthy gap ever measured on this path (p50 ~1s,
// p95 1.2s, max 3.5s over 8 minutes across three tables, with ZERO gaps beyond
// 10s - see the multi-seat harness notes). So it is still well clear of normal
// jitter while landing inside a single turn.
//
// The load stays bounded because this applies to exactly ONE table per client:
// a client cannot be acting on two tables at once. So the worst case adds a
// single early subscribe per client per episode, and the per-table floor and the
// client-wide breaker are untouched - the ceiling is the same, only the latency
// on the one table that matters changes.
export const DEFAULT_PRIORITY_SILENCE_MS = 6_000;
// Narrower than the background jitter (0-10s) because it is spreading one
// request per client rather than up to four, and a wide spread here would give
// back the latency this exists to win. 5s still keeps a fleet-wide burst at
// roughly a quarter of the subscribe Lambda's observed concurrency ceiling.
export const DEFAULT_PRIORITY_JITTER_MS = 5_000;
// A table that had a feed and lost it across a reconnect is judged against this
// rather than the full silence threshold. Frames resume within a couple of the
// ~3s broadcast cadence on a healthy re-subscribe, so ~3 cadences is already
// conclusive, and waiting the full 20s here costs a whole production turn clock
// on the failure this exists for: a reconnect whose re-subscribes were silently
// rejected, which leaves the client frameless until it gets a NEW connectionId.
// The cost of being wrong is one extra targeted subscribe, once per reconnect
// per table.
export const DEFAULT_POST_RECONNECT_GRACE_MS = 8_000;
// After a repair, how long a still-frameless table waits before exhaustedTables()
// reports it. A repair that worked is proven by a frame; on a live table that
// arrives within a cadence or two. Nothing this watchdog can do fixes a table
// still silent after this, because its only tool is the subscribe that was just
// refused - see exhaustedTables().
export const DEFAULT_ESCALATE_AFTER_REPAIR_MS = 8_000;
export const DEFAULT_REPAIR_WINDOW_MS = 5 * 60_000;

export class TableFeedWatchdog {
  private readonly silenceMs: number;
  private readonly jitterMs: number;
  private readonly minRepairGapMs: number;
  private readonly maxRepairsPerWindow: number;
  private readonly maxRepairsPerEpisode: number;
  private readonly postReconnectGraceMs: number;
  private readonly prioritySilenceMs: number;
  private readonly priorityJitterMs: number;
  /** The table the player is acting on, judged on the tighter clock. */
  private priorityGameID: string | null = null;
  private readonly escalateAfterRepairMs: number;
  private readonly windowMs: number;
  private readonly random: () => number;

  private readonly tables = new Map<string, TableFeedState>();
  /** Timestamps of every repair issued, pruned to `windowMs`. */
  private repairs: number[] = [];
  private _breakerOpen = false;
  private _repairCount = 0;
  private _refusedCount = 0;

  constructor(options: TableFeedWatchdogOptions = {}) {
    this.silenceMs = options.silenceMs ?? DEFAULT_FEED_SILENCE_MS;
    this.jitterMs = options.jitterMs ?? DEFAULT_FEED_JITTER_MS;
    this.minRepairGapMs = options.minRepairGapMs ?? DEFAULT_MIN_REPAIR_GAP_MS;
    this.maxRepairsPerWindow = options.maxRepairsPerWindow ?? DEFAULT_MAX_REPAIRS_PER_WINDOW;
    this.maxRepairsPerEpisode = options.maxRepairsPerEpisode ?? DEFAULT_MAX_REPAIRS_PER_EPISODE;
    this.postReconnectGraceMs = options.postReconnectGraceMs ?? DEFAULT_POST_RECONNECT_GRACE_MS;
    this.prioritySilenceMs = options.prioritySilenceMs ?? DEFAULT_PRIORITY_SILENCE_MS;
    this.priorityJitterMs = options.priorityJitterMs ?? DEFAULT_PRIORITY_JITTER_MS;
    this.escalateAfterRepairMs = options.escalateAfterRepairMs ?? DEFAULT_ESCALATE_AFTER_REPAIR_MS;
    this.windowMs = options.windowMs ?? DEFAULT_REPAIR_WINDOW_MS;
    this.random = options.random ?? Math.random;
  }

  /**
   * Start watching a table.
   *
   * The silence clock deliberately does NOT start here - it starts at the first
   * frame, and until then this watchdog ignores the table entirely.
   *
   * Why: a targeted re-subscribe cannot deliver a table's FIRST frame.
   * `subscribeGame` pushes a snapshot only when the subscription is new for that
   * connection (`isNewSubscription`), so re-subscribing a connection that
   * already holds the row is a no-op snapshot-wise, and the row churn can get in
   * the way of an initial snapshot still in flight. Firing on a never-hydrated
   * table is useless work at best.
   *
   * A table that has never hydrated has a different owner:
   * `hooks/useFeltHydrationRecovery` forces one reconnect, which yields a fresh
   * connectionId, which makes the subscription genuinely new, which is what
   * produces a snapshot. This watchdog only does the narrower job it can
   * actually do: a feed that was proven alive and then stopped.
   *
   * This also removes the "reduced grace for a mid-session subscribe" problem
   * raised in review - there is no grace window to get wrong, because the clock
   * starts when the table proves itself.
   */
  track(gameID: string | number): void {
    const key = String(gameID);
    if (this.tables.has(key)) return;
    this.tables.set(key, {
      lastFrameAt: null,
      everHadFrame: false,
      graceUntil: null,
      episodeRepairs: 0,
      dueAt: null,
      lastRepairAt: Number.NEGATIVE_INFINITY,
      escalated: false
    });
  }

  /**
   * Name the table the player is ACTING on, or null when they are not acting.
   *
   * That table is judged against `prioritySilenceMs` instead of `silenceMs`,
   * and its repair carries the narrower `priorityJitterMs`. Everything else -
   * the per-table floor, the client-wide breaker, the episode allowance, the
   * escalation - is identical, so this changes WHEN a dead feed is noticed on
   * one table and nothing about how much traffic the client can generate.
   *
   * Exactly one table can hold this at a time, which is what keeps the tighter
   * clock affordable: a player cannot be acting on two tables simultaneously.
   */
  setPriority(gameID: string | number | null): void {
    const key = gameID === null || gameID === undefined ? null : String(gameID);
    if (key === this.priorityGameID) return;
    this.priorityGameID = key;
    // A table moving onto (or off) the tighter clock has its pending decision
    // re-taken on the next tick: a dueAt computed under the old threshold's
    // jitter would otherwise hold a now-priority table back for up to 10s,
    // which is the whole latency this is meant to remove.
    this.tables.forEach((state) => {
      state.dueAt = null;
    });
  }

  /**
   * Has this table ever produced a frame on this client?
   *
   * Exposed so the subscription heartbeat can skip a table a re-subscribe
   * provably cannot help. `subscribeGame` pushes a snapshot only when the
   * subscription is NEW for the connection, so re-asserting a row this
   * connection already holds delivers nothing - which is exactly why this
   * watchdog refuses to judge such a table (see track()). The heartbeat had no
   * equivalent guard and beat every tracked table, so a table waiting on its
   * first frame paid a full subscribe every 10s (a MySQL pool connection plus
   * three queries plus two DynamoDB ops) for no possible benefit. A table that
   * has never hydrated belongs to hooks/useFeltHydrationRecovery, whose forced
   * reconnect yields a fresh connectionId and therefore a genuinely new
   * subscription.
   *
   * Unknown tables report false: an untracked table is not ours to beat.
   */
  hasProvenFeed(gameID: string | number): boolean {
    return this.tables.get(String(gameID))?.everHadFrame ?? false;
  }

  /**
   * How long this table's feed has been silent, or null when there is nothing
   * to measure (never tracked, or never produced a frame and not inside a
   * post-reconnect grace).
   *
   * Exposed so a caller can ask the one question that decides whether
   * re-asserting a subscription can possibly help: is this table still
   * receiving frames? A table that is, has a working fan-out row by definition,
   * so re-writing that row repairs nothing - see ConnectionManager.resyncTable.
   */
  msSinceLastFrame(gameID: string | number, now: number): number | null {
    const state = this.tables.get(String(gameID));
    if (!state) return null;
    return this.silentForMs(state, now);
  }

  /** Diagnostic: which table currently holds the tighter clock. */
  get priorityTable(): string | null {
    return this.priorityGameID;
  }

  untrack(gameID: string | number): void {
    this.tables.delete(String(gameID));
  }

  /** A table frame arrived. The table is alive; any pending repair is cancelled. */
  noteFrame(gameID: string | number, now: number): void {
    const state = this.tables.get(String(gameID));
    if (!state) return;
    state.lastFrameAt = now;
    state.everHadFrame = true;
    state.graceUntil = null;
    state.episodeRepairs = 0;
    state.dueAt = null;
    state.escalated = false;
  }

  /**
   * The update socket (re)connected. Every table must PROVE its feed again
   * before this watchdog will judge it.
   *
   * A table that has NEVER produced a frame is still left alone entirely: a
   * targeted re-subscribe cannot deliver a first frame, and that case belongs to
   * `hooks/useFeltHydrationRecovery`. See track().
   *
   * A table that HAD a feed is judged again, against `postReconnectGraceMs`
   * rather than the full `silenceMs`. This is the case EN-10628's QA pass fell
   * through, and it fell through here: nulling `lastFrameAt` for every table made
   * the two indistinguishable, so a reconnect whose `resubscribeAll` was
   * rejected left every table permanently invisible to this watchdog - it never
   * fired once across a measured 71s of a completely frameless client, while the
   * only repairs on the wire came from PokerActions holding an action. Nothing
   * else covers it either: `useFeltHydrationRecovery` keys on a felt that never
   * hydrated, and these tables had hydrated and were rendering stale state, so
   * the app had to be restarted to recover - exactly what QA reported.
   *
   * The grace is shorter than `silenceMs` because a reconnect gives an exact
   * reference point: on a healthy re-subscribe frames resume within a cadence or
   * two of the ~3s broadcast interval, so there is no hiccup to ride out here.
   */
  resetClock(now?: number): void {
    this.tables.forEach((state) => {
      state.lastFrameAt = null;
      state.episodeRepairs = 0;
      state.dueAt = null;
      state.escalated = false;
      // Only a table with something to re-prove gets a grace window; one that
      // never hydrated stays outside this watchdog's remit.
      state.graceUntil =
        state.everHadFrame && typeof now === 'number' ? now + this.postReconnectGraceMs : null;
    });
  }

  /**
   * How long this table has been without a frame, or null when there is nothing
   * to measure (it has never produced one, and no reconnect grace applies).
   *
   * Inside a post-reconnect grace window `lastFrameAt` is null but the reference
   * point is the reset itself, which `graceUntil` encodes.
   */
  /** The silence threshold this table is judged against. */
  private silenceForTable(gameID: string): number {
    return gameID === this.priorityGameID ? this.prioritySilenceMs : this.silenceMs;
  }

  /** The jitter this table's repair carries. */
  private jitterForTable(gameID: string): number {
    return gameID === this.priorityGameID ? this.priorityJitterMs : this.jitterMs;
  }

  private silentForMs(state: TableFeedState, now: number): number | null {
    if (state.lastFrameAt !== null) return now - state.lastFrameAt;
    if (state.graceUntil === null) return null;
    return now - (state.graceUntil - this.postReconnectGraceMs);
  }

  /**
   * Has this table lost its feed? Judged against the post-reconnect grace while
   * one is open, and against the full silence threshold otherwise.
   */
  private isDead(gameID: string, state: TableFeedState, now: number): boolean {
    if (state.lastFrameAt === null) {
      // A post-reconnect grace already runs on a short clock; the priority
      // table takes whichever of the two is tighter so it is never made SLOWER
      // by being inside a grace window.
      if (state.graceUntil === null) return false;
      const due =
        gameID === this.priorityGameID
          ? Math.min(
              state.graceUntil,
              state.graceUntil - this.postReconnectGraceMs + this.prioritySilenceMs
            )
          : state.graceUntil;
      return now >= due;
    }
    return now - state.lastFrameAt >= this.silenceForTable(gameID);
  }

  /**
   * Tables this watchdog has repaired and cannot help any further: still no
   * frame, `escalateAfterRepairMs` after the repair went out.
   *
   * This exists because a repair is a REQUEST that the subscribe Lambda answers
   * with nothing at all - no route response is configured and it sends no
   * confirmation - so "was my repair accepted?" is unanswerable from here. What
   * IS observable is the consequence: on a live table a written fan-out row
   * produces a frame within a cadence or two whatever else is happening, so
   * continued silence after a repair means the row was not written. Measured in
   * develop on the EN-10628 QA build: 9.1% of SubscribeToGameUpdates calls were
   * rejected in under 50ms with no log line, and 11 of 104 connections never had
   * a single subscribe accepted - one of them re-subscribing 12 times over 71s
   * into a wall.
   *
   * Re-sending the same subscribe cannot fix that, because the request is
   * per-connection: only a NEW connectionId makes the subscription genuinely new.
   * So this reports the table and the OWNER decides - deliberately, because a
   * reconnect blanks every table to fix one and must be coalesced and rate-limited
   * across the client, which is not a per-table decision. Reported once per
   * silence episode.
   */
  exhaustedTables(now: number): string[] {
    const out: string[] = [];
    this.tables.forEach((state, gameID) => {
      if (state.escalated) return;
      if (state.episodeRepairs === 0) return;
      if (!this.isDead(gameID, state, now)) return;
      if (now - state.lastRepairAt < this.escalateAfterRepairMs) return;
      state.escalated = true;
      out.push(gameID);
    });
    return out;
  }

  /**
   * Evaluate every tracked table. Returns the tables whose feed is dead and whose
   * repair is due now. The caller performs the repair; this only decides.
   *
   * `canRepair` is asked BEFORE any state is spent. If the caller cannot
   * actually perform the repair for a table (raised in review: the empty-GUID
   * guard refuses to put a subscribe on the wire), that table is skipped with
   * its episode and breaker credit untouched, so a later tick tries again once
   * the reason clears. Marking it repaired would leave it permanently
   * unrepairable while the watchdog believed it had done the work.
   */
  tick(now: number, canRepair?: (gameID: string) => boolean): TableFeedRepair[] {
    const due: TableFeedRepair[] = [];
    this.repairs = this.repairs.filter((at) => now - at < this.windowMs);
    if (this._breakerOpen && this.repairs.length < this.maxRepairsPerWindow) {
      this._breakerOpen = false;
    }

    // Tables whose feed has died, in the order their repairs should be allowed
    // to spend the breaker's budget: every table awaiting its FIRST repair
    // before any table asking for a REPEAT.
    //
    // Why the order matters. The breaker's budget is the four-table cap, chosen
    // so a client can always repair all of its own tables in one pass. Once a
    // table may be repaired more than once per episode, insertion order alone
    // lets one table's second and third attempts consume that budget while
    // another table still has zero, and the loser then waits out the whole
    // rolling window for its first attempt (PR review: 680s instead of 220s for
    // a dead background table). A first repair is also strictly more valuable
    // than a repeat, since a repeat is by definition retrying something that has
    // already been asked for once.
    const dead: Array<{ gameID: string; state: TableFeedState; silentForMs: number }> = [];
    this.tables.forEach((state, gameID) => {
      const silentForMs = this.silentForMs(state, now);
      // Nothing to measure: the table has never produced a frame at all, so it
      // is not this watchdog's problem (see track() and resetClock()).
      if (silentForMs === null) return;
      if (!this.isDead(gameID, state, now)) {
        state.dueAt = null;
        return;
      }
      if (state.episodeRepairs >= this.maxRepairsPerEpisode) return;
      dead.push({ gameID, state, silentForMs });
    });
    dead.sort((a, b) => a.state.episodeRepairs - b.state.episodeRepairs);

    dead.forEach(({ gameID, state, silentForMs }) => {
      if (state.dueAt === null) {
        state.dueAt = now + Math.floor(this.random() * this.jitterForTable(gameID));
      }
      if (now < state.dueAt) return;

      // Due. The floor and the breaker DEFER the repair rather than cancel it:
      // the table stays pending (dueAt kept) and fires on a later tick once the
      // gap has elapsed or the window has freed. Only an actual repair spends
      // the episode.
      if (now - state.lastRepairAt < this.minRepairGapMs) return;
      if (this.repairs.length >= this.maxRepairsPerWindow) {
        this._breakerOpen = true;
        return;
      }
      // Asked last, but before anything is spent.
      if (canRepair && !canRepair(gameID)) {
        this._refusedCount += 1;
        return;
      }

      state.episodeRepairs += 1;
      state.dueAt = null;
      this.repairs.push(now);
      this._repairCount += 1;
      state.lastRepairAt = now;
      due.push({ gameID, silentForMs });
    });

    return due;
  }

  /** Total repairs issued over the watchdog's lifetime. Diagnostic only. */
  get repairCount(): number {
    return this._repairCount;
  }

  /**
   * How many times a due repair was refused by `canRepair`. Diagnostic only, and
   * the observable signal review asked for: a table that is due but permanently
   * unrepairable shows up here instead of looking like silence.
   */
  get refusedCount(): number {
    return this._refusedCount;
  }

  /** True while the rolling-window breaker is refusing further repairs. */
  get breakerOpen(): boolean {
    return this._breakerOpen;
  }

  get trackedCount(): number {
    return this.tables.size;
  }
}
